import { createElement as _createElement } from "react";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment, forwardRef } from 'react';
import { BKJChip, BKJIcon } from '../index.js';
import { Flex } from '../Flex/index.js';
import { BKJPillDropdownProvider, useBKJPillDropdownContext } from './BKJPillDropdown.context.js';
import { ArrowIconWrapper, BKJPillDropdownComboBox, BKJPillDropdownWrapper, InputLabel, InputMessage, RequiredAsterisk, TextAreaWrapper, } from './BKJPillDropdown.styled.js';
import i18next from 'i18next';
import { SELECT_ALL_NODE_ID } from './BKJPillDropdown.constants.js';
import { useResizeObserver } from '../../hooks/index.js';
const InputComponent = forwardRef(({ onInputChange, value, isFocused, onIsOpenChange, options = [], wrapperRef, placeholder, ...rest }, ref) => {
    const [, height] = useResizeObserver(wrapperRef?.current);
    return (_jsx(BKJPillDropdownComboBox, { ...rest, "$dropdownOffset": height || 0, overrides: ['HIDE_VALUE_LABEL', 'CHECKBOX_VARIANT'], ref: ref, action: "OpenToggle", name: "BKJPillDropdown_InputValue", placeholder: !!value?.length ? '' : placeholder, options: options, type: "Multiple", onIsOpenChange: onIsOpenChange, onChange: onInputChange, value: value, hasValue: value.length > 0, isFocused: isFocused }));
});
const BKJPillDropdownComponent = (props) => {
    const { value, label, required, disabled, description, name, error, max, options, placeholder, showSelectAll, allowedAutoPlacements, ...rest } = props;
    const { wrapperRef, inputRef, uuid, onChipRemove, onInputFocused, onInputChange, onInputBlur, isMaxLimitReached, onIsOpenChange, isFocused, } = useBKJPillDropdownContext();
    const dropdownListOptions = [
        ...(showSelectAll && options?.length ? [{
                name: SELECT_ALL_NODE_ID,
                id: SELECT_ALL_NODE_ID,
                key: SELECT_ALL_NODE_ID,
                label: i18next.t('translations:SELECT_ALL'),
                value: SELECT_ALL_NODE_ID,
            }] : []),
        ...options
    ];
    const handleSelectAllChange = (event, dropdownListOptions, onInputChange) => {
        if (event.target.id === SELECT_ALL_NODE_ID) {
            const allValues = dropdownListOptions.map(({ value }) => value);
            return onInputChange({ ...event, target: { ...event.target, name, value: allValues } });
        }
        else {
            const filteredValue = event.target.value.filter((value) => value !== SELECT_ALL_NODE_ID);
            return onInputChange({ ...event, target: { ...event.target, name, value: filteredValue } });
        }
    };
    const handleGroupChange = (event, _, onInputChange) => {
        const groupOptions = options.filter((option) => option?.groupId === event.target.groupId).map(({ value }) => value);
        const allGroupOptionsExceptCurrent = groupOptions.filter((value) => value !== event.target.id);
        const shouldClearValues = allGroupOptionsExceptCurrent.every((value) => event.target.value.includes(value));
        if (shouldClearValues) {
            const filteredValue = event.target.value.filter((value) => !groupOptions.includes(value));
            return onInputChange({ ...event, target: { ...event.target, name, value: filteredValue } });
        }
        else {
            const selectedOptions = options.filter((option) => option?.groupId === event.target.groupId || event.target.value.includes(option.value)).map(({ value }) => value);
            return onInputChange({ ...event, target: { ...event.target, name, value: selectedOptions } });
        }
    };
    const handleNestedGroupChange = (event, _, onInputChange) => {
        const filteredValue = event.target.value.filter((value) => {
            const option = options.find((opt) => opt.value === value);
            return !(option && option.groupId === event.target.groupId && option.isGroupNode);
        });
        const nestedGroupOptions = options
            .filter((option) => option.groupId === event.target.groupId && !option.isGroupNode);
        if (nestedGroupOptions.every((value) => filteredValue.includes(value))) {
            const groupNode = options.find((opt) => opt.groupId === event.target.groupId && opt.isGroupNode);
            if (!filteredValue.includes(groupNode.value)) {
                filteredValue.push(groupNode.value);
            }
        }
        return onInputChange({ ...event, target: { ...event.target, name, value: filteredValue } });
    };
    return (_jsxs(BKJPillDropdownWrapper, { children: [label && (_jsxs(InputLabel, { disabled: disabled, children: [label, required && _jsx(RequiredAsterisk, { children: "\u00A0*" })] })), value.length !== 0 && (_createElement(TextAreaWrapper, { ...rest, key: `BKJPillDropdown_${uuid}`, id: `BKJPillDropdown_${uuid}_TextAreaWrapper`, ref: wrapperRef, error: !!error || isMaxLimitReached, disabled: disabled, onMouseDown: onInputFocused, hasValue: value.length > 0, isFocused: isFocused },
                _jsxs(Flex, { flexWrap: "wrap", gap: "4px", padding: value.length > 0 ? '8px 8px 0' : '8px 8px', margin: "0 17px 0 0", children: [value.map((strValue) => {
                            const MATCHED = options.find(({ value, isGroupNode }) => value === strValue && !isGroupNode);
                            if (!MATCHED)
                                return _jsx(Fragment, {});
                            return (_jsx(BKJChip, { onClose: onChipRemove(MATCHED.value), variant: "Purple", children: MATCHED.label }, MATCHED.value));
                        }), _jsx(ArrowIconWrapper, { children: _jsx(BKJIcon, { iconName: isFocused ? 'ArrowUp' : 'ArrowDown' }) })] }))), _jsx(InputComponent, { ref: inputRef, options: dropdownListOptions, isFocused: isFocused, value: value, onInputChange: (event) => {
                    if (showSelectAll && event.target.value.includes(SELECT_ALL_NODE_ID)) {
                        return handleSelectAllChange(event, dropdownListOptions, onInputChange);
                    }
                    else if (showSelectAll && event.target.id === SELECT_ALL_NODE_ID) {
                        return onInputChange({ ...event, target: { ...event.target, value: [] } });
                    }
                    else if (event.target?.groupId && event.target?.isGroupNode) {
                        return handleGroupChange(event, options, onInputChange);
                    }
                    else if (event.target?.groupId && !event.target?.isGroupNode) {
                        return handleNestedGroupChange(event, options, onInputChange);
                    }
                    else {
                        return onInputChange(event);
                    }
                }, onBlur: onInputBlur, onIsOpenChange: onIsOpenChange, placeholder: placeholder, allowedAutoPlacements: allowedAutoPlacements, wrapperRef: wrapperRef }), description && _jsx(InputMessage, { children: description })] }));
};
export const BKJPillDropdown = ({ value = [], onChange, name, validations, max = 10, placeholder = '', showSelectAll = false, ...rest }) => {
    return (_jsx(BKJPillDropdownProvider, { value: value, onChange: onChange, name: name, validations: validations, max: max, children: _jsx(BKJPillDropdownComponent, { name: name, value: value, max: max, ...rest, showSelectAll: showSelectAll, placeholder: placeholder }) }));
};
//# sourceMappingURL=BKJPillDropdown.js.map